import { Center, Loader, Stack, Title } from '@mantine/core'
import { FormattedMessage } from 'react-intl'
import { ErrorWithReload } from '@/components/ui-shared/ErrorWithReload/ErrorWithReload'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetDeploymentAlertSettings } from '@/queries/deploymentAlertQueries'
import { useGetDeviceList } from '@/queries/deviceQueries'
import { CrudAction, Resource } from '@/types/permissions'
import { MAX_ITEMS_WITHOUT_PAGINATION } from '@/utils/pagination'
import { NotificationRulesHandler } from './NotificationRulesHandler'

const deviceListParams = {
  limit: MAX_ITEMS_WITHOUT_PAGINATION,
  ordering: 'label'
}

const alertSettingListParams = { limit: MAX_ITEMS_WITHOUT_PAGINATION }

export const WorkspaceNotificationsPage = () => {
  const [canReadAlertSettings] = useHasPermissions(
    Resource.DeploymentAlertSettings,
    [CrudAction.Read]
  )

  // Saving a rule set can create, update and delete in one go, so the editing UI
  // needs all three rather than update alone.
  const [canCreate, canUpdate, canDelete] = useHasPermissions(
    Resource.DeploymentAlertSettings,
    [CrudAction.Create, CrudAction.Update, CrudAction.Delete]
  )

  const {
    data: alertSettingsData,
    isLoading,
    isError,
    refetch
  } = useGetDeploymentAlertSettings(alertSettingListParams, {
    enabled: canReadAlertSettings
  })

  const {
    data: deviceData,
    isLoading: isLoadingDevices,
    isError: isDevicesError,
    refetch: refetchDevices
  } = useGetDeviceList(deviceListParams)

  if (!canReadAlertSettings) {
    return (
      <Stack gap="xs">
        <Title order={3}>
          <FormattedMessage id="workspace.tabs.notifications" />
        </Title>

        <PlatformAlert variant="warning">
          <FormattedMessage id="workspace.notifications.noPermission" />
        </PlatformAlert>
      </Stack>
    )
  }

  if (isLoading) {
    return (
      <Center h={200}>
        <Loader size="md" />
      </Center>
    )
  }

  if (isError) {
    return (
      <ErrorWithReload onReload={() => void refetch()}>
        <FormattedMessage id="workspace.notifications.data.error" />
      </ErrorWithReload>
    )
  }

  if (isDevicesError) {
    return (
      <ErrorWithReload onReload={() => void refetchDevices()}>
        <FormattedMessage id="devices.data.error" />
      </ErrorWithReload>
    )
  }

  return (
    <NotificationRulesHandler
      alertSettings={alertSettingsData?.results || []}
      devices={deviceData?.results || []}
      isLoadingDevices={isLoadingDevices}
      canManage={canCreate && canUpdate && canDelete}
    />
  )
}
